"use client";

import { Check, CreditCard, ExternalLink, InfinityIcon, Shield, Sparkles } from "lucide-react";
import { Badge } from "@baluu/ui/components/badge";
import { Button } from "@baluu/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@baluu/ui/components/card";
import {
  formatMembershipPrice,
  membershipPerks,
  membershipPlanBadge,
  type MembershipPlan,
} from "@baluu/ui/lib/checkout/purchases";

export type MembershipPlanCardProps = {
  plan: MembershipPlan;
  /** Start the subscription (Stripe Checkout). */
  onSubscribe: (plan: MembershipPlan) => void;
  /** Open the customer portal for an active subscription. */
  onManage?: (plan: MembershipPlan) => void;
  /** This plan's subscribe call is in flight. */
  subscribing?: boolean;
  /** This plan's portal call is in flight. */
  managing?: boolean;
  /** Another plan's call is in flight: hold this one's buttons too. */
  busy?: boolean;
};

/**
 * One membership plan with its price, what it gives and the button that
 * starts it — or, for the buyer's own active plan, "Active member" and a way
 * to manage it. The hosted booking site's /memberships page and the
 * membership web component both draw it.
 */
export function MembershipPlanCard({ plan, onSubscribe, onManage, subscribing, managing, busy }: MembershipPlanCardProps) {
  const Icon = plan.type === "credit" ? CreditCard : plan.type === "unlimited" ? InfinityIcon : Shield;
  return (
    <Card className="relative flex flex-col overflow-hidden">
      {plan.trialPeriodDays ? (
        <div className="absolute right-3 top-3">
          <Badge variant="secondary" className="gap-1">
            <Sparkles className="h-3 w-3" aria-hidden />
            {plan.trialPeriodDays}-day free trial
          </Badge>
        </div>
      ) : null}

      <CardHeader className="pb-2">
        <div className="mb-1 flex items-center gap-2">
          <Icon className="h-5 w-5 text-primary" aria-hidden />
          <Badge variant="outline" className="text-xs">
            {membershipPlanBadge(plan)}
          </Badge>
        </div>
        <CardTitle className="text-xl">{plan.name}</CardTitle>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col">
        {plan.description ? <p className="mb-4 text-sm text-muted-foreground">{plan.description}</p> : null}

        <div className="mb-4">
          <span className="text-3xl font-bold">{formatMembershipPrice(plan.priceInCents, plan.currency)}</span>
          <span className="text-muted-foreground">/{plan.billingInterval === "yearly" ? "yr" : "mo"}</span>
          {/* Only calendar-month plans need explaining; "renews on the date
              you join" is what customers already expect. */}
          {plan.cycle?.cycleAlignment === "month_start" ? (
            <p className="mt-1 text-xs text-muted-foreground">{plan.cycle.description}</p>
          ) : null}
        </div>

        <ul className="mb-6 flex-1 space-y-2">
          {membershipPerks(plan).map((perk) => (
            <li key={perk} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-green-500" aria-hidden />
              {perk}
            </li>
          ))}
        </ul>

        {plan.isSubscribed ? (
          <div className="space-y-2">
            <Badge variant="default" className="w-full justify-center py-1.5 text-sm">
              Active Member
            </Badge>
            {/* Every subscribed plan offers Manage, as the hosted page always
                did; the portal needs the subscription, so without one (a
                response that didn't name it) the button is there but held. */}
            {onManage ? (
              <Button
                variant="outline"
                className="w-full gap-1"
                onClick={() => onManage(plan)}
                disabled={managing || busy || !plan.subscriptionId}
              >
                <ExternalLink className="h-3 w-3" aria-hidden />
                {managing ? "Loading..." : "Manage Subscription"}
              </Button>
            ) : null}
          </div>
        ) : (
          <Button className="w-full" onClick={() => onSubscribe(plan)} disabled={subscribing || busy}>
            {subscribing ? "Loading..." : plan.trialPeriodDays ? "Start Free Trial" : "Subscribe"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
